import { Injectable, effect, inject, untracked } from '@angular/core';
import { RouterStateSnapshot, TitleStrategy } from '@angular/router';
import { EgBrandService } from '@expertgroup/shell';
import { I18n } from '@/app/core/i18n/i18n';
import { routeCrumbs } from '@/app/core/navigation/route-crumbs';

/**
 * Tab naslov prati aktivnu stranicu i masku: „Tenanti · Identity". Stranica je poslednja mrvica rute (`data.breadcrumb`,
 * isti izvor kao mrvice u zaglavlju, `routeCrumbs`), pa tab nosi isti naziv kao stranica koju prikazuje. Oblik
 * „Stranica · Proizvod" i ime proizvoda daje maska (ljuska 0.15, `EgBrandService.title`): „Identity" iz
 * `provideIdentityBrand`. Maska hosta (0.16) ime proizvoda ne nosi, pa ga ni na domenu tenanta ne menja.
 *
 * Rute Identity-ja nemaju polje `title`, pa podrazumevana strategija Angulara naslov nikad ne bi postavila; ovde se naslov
 * postavlja na svakoj navigaciji. Ruta bez mrvice (početna `''`, koja šalje persone na njihove stranice, i „nije
 * pronađeno") daje samo ime proizvoda.
 *
 * Mrvica je ključ prevoda ili običan tekst (ime tenanta), pa se prevodi pri svakom postavljanju; promena jezika prepisuje
 * naslov bez nove navigacije.
 */
@Injectable({ providedIn: 'root' })
export class IdentityTitleStrategy extends TitleStrategy {
    private readonly brand = inject(EgBrandService);
    private readonly i18n = inject(I18n);
    private lastSnapshot?: RouterStateSnapshot;

    constructor() {
        super();
        // The language is tracked here and only here: the page is re-read in the new language the moment it changes. The
        // mask re-applies the title itself when IT changes, so nothing of the brand is tracked (`untracked`).
        effect(() => {
            this.i18n.locale();
            const page = this.pageTitle(this.lastSnapshot);
            untracked(() => this.brand.title(page));
        });
    }

    override updateTitle(snapshot: RouterStateSnapshot): void {
        this.lastSnapshot = snapshot;
        this.brand.title(this.pageTitle(snapshot));
    }

    private pageTitle(snapshot: RouterStateSnapshot | undefined): string {
        const crumbs = snapshot ? routeCrumbs(snapshot.root) : [];
        const page = crumbs[crumbs.length - 1];
        return page ? this.i18n.routeText(page) : '';
    }
}
